import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  AlertTriangle,
  CheckCircle2,
  Database as DatabaseIcon,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { cn, type components, formatBytes, getAdminStorageHandlers } from "@comandero/shared";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";

const SKELETON_COUNT = 5;

export function StorageManager() {
  const { t } = useI18n();
  const [filter, setFilter] = createSignal<"all" | "orphaned" | "linked">("all");

  const objectsQuery = createApiQuery<
    components["schemas"]["S3ObjectDTO"][],
    components["schemas"]["ErrorModel"]
  >(
    () => ["admin", "s3-objects"],
    async () => api.GET("/api/admin/font/listS3Objects", {}),
    () => ({ staleTime: 0 }),
  );

  const pruneMutation = createApiMutation<
    undefined,
    components["schemas"]["PruneResult"],
    components["schemas"]["ErrorModel"]
  >(async () => api.POST("/api/admin/font/pruneOrphanedObjects"));

  const handlers = createMemo(() =>
    getAdminStorageHandlers({
      objects: objectsQuery.data ?? [],
      onRefetch: () => {
        void objectsQuery.refetch();
      },
      onPrune: () => {
        // This is handled by the ConfirmDialog wrapper in the UI
      },
    }),
  );

  const stats = () => handlers().getStats();
  const filteredObjects = () => handlers().getFilteredObjects(filter());

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <DatabaseIcon class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`S3 Storage`)}</h2>
        </div>
        <div class="flex gap-2">
          <Button
            variant="outline"
            onClick={() => void objectsQuery.refetch()}
            disabled={objectsQuery.isLoading}
          >
            <RefreshCw class={cn("mr-2 h-4 w-4", objectsQuery.isLoading && "animate-spin")} />
            {t(msg`Refresh`)}
          </Button>
          <ConfirmDialog
            title={t(msg`Clean orphaned objects?`)}
            description={t(
              "Are you sure you want to delete orphaned objects? This action cannot be undone and will free up space in S3.",
            )}
            confirmLabel={t(msg`Clean`)}
            variant="destructive"
            onConfirm={() => {
              pruneMutation.mutate(undefined, {
                onSuccess: (result) => {
                  const { deleted } = result;
                  showToast(t(msg`Successfully deleted ${deleted} orphaned objects.`), "success");
                  void objectsQuery.refetch();
                },
              });
            }}
            renderTrigger={(open) => (
              <Button
                variant="destructive"
                onClick={open}
                disabled={pruneMutation.isPending || stats().orphanedCount === 0}
              >
                <Show when={pruneMutation.isPending} fallback={<Trash2 class="mr-2 h-4 w-4" />}>
                  <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                </Show>
                {t(msg`Clean Orphans`)} ({stats().orphanedCount})
              </Button>
            )}
          />
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent class="pt-6 text-center">
            <p class="text-3xl font-bold">{stats().totalCount}</p>
            <p class="text-xs text-muted-foreground uppercase tracking-wider mt-1">
              {t(msg`Total Objects`)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent class="pt-6 text-center">
            <p
              class={cn(
                "text-3xl font-bold",
                stats().orphanedCount > 0 ? "text-amber-500" : "text-success",
              )}
            >
              {stats().orphanedCount}
            </p>
            <p class="text-xs text-muted-foreground uppercase tracking-wider mt-1">
              {t(msg`Orphans`)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent class="pt-6 text-center">
            <p class="text-3xl font-bold">{formatBytes(stats().totalSize)}</p>
            <p class="text-xs text-muted-foreground uppercase tracking-wider mt-1">
              {t(msg`Total Space`)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-2 pb-2">
          <For each={["all", "linked", "orphaned"] as const}>
            {(f) => (
              <Button
                variant={filter() === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
                class="rounded-full px-4"
              >
                {{ all: t(msg`All`), linked: t(msg`Linked`), orphaned: t(msg`Orphaned`) }[f]}
                <Badge variant="secondary" class="ml-2 bg-background/20">
                  {stats().getLabelCount(f)}
                </Badge>
              </Button>
            )}
          </For>
        </div>

        <Show
          when={!objectsQuery.isLoading}
          fallback={
            <div class="grid gap-3">
              <For each={Array.from({ length: SKELETON_COUNT })}>
                {() => <Skeleton class="h-16 w-full rounded-lg" />}
              </For>
            </div>
          }
        >
          <div class="grid gap-3">
            <For
              each={filteredObjects()}
              fallback={
                <div class="py-20 text-center text-muted-foreground">
                  <Search class="mx-auto h-12 w-12 opacity-10 mb-4" />
                  <p>{t(msg`No objects matching the filter.`)}</p>
                </div>
              }
            >
              {(obj) => (
                <div
                  class={cn(
                    "flex flex-col gap-1 rounded-lg border p-4 transition-colors",
                    obj.isOrphaned
                      ? "border-amber-500/20 bg-amber-500/5"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2 min-w-0">
                      <Show
                        when={obj.isOrphaned}
                        fallback={<CheckCircle2 class="h-4 w-4 text-success shrink-0" />}
                      >
                        <AlertTriangle class="h-4 w-4 text-amber-500 shrink-0" />
                      </Show>
                      <span class="font-mono text-xs truncate">{obj.filename}</span>
                    </div>
                    <div class="flex gap-1 shrink-0">
                      <Badge variant="outline" class="text-[9px] uppercase">
                        {obj.type}
                      </Badge>
                      <Show when={obj.isOrphaned}>
                        <Badge
                          variant="outline"
                          class="text-[9px] uppercase bg-amber-500/10 text-amber-600 border-amber-500/20"
                        >
                          {t(msg`Orphaned`)}
                        </Badge>
                      </Show>
                    </div>
                  </div>
                  <div class="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span>{formatBytes(obj.size)}</span>
                    <Show when={obj.lastModified}>
                      <span>• {new Date(obj.lastModified).toLocaleDateString()}</span>
                    </Show>
                    <Show when={!obj.isOrphaned}>
                      <span class="text-success">• {obj.fontFamilyName}</span>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
}
